import React, { useEffect } from 'react';
import _ from 'lodash';
import { compose } from 'redux';
import { PropTypes } from 'prop-types';

import {
  withFormik,
  Field,
  Form as FormikForm,
} from 'formik';

import {
  Form,
} from 'semantic-ui-react';

import { globalTheme, InputFormField } from '@playbookux/playbook-web-core';
import {
  age,
  gender,
  ethnicity,
  householdIncome,
  workStatus,
  industry,
  seniority,
  companySize,
  states as locationState,
  countries as locationCountry,
} from '../audience/options';

import schema from './schema';
import Audience from '../audience';

import {
  ContainerDefault,
  Container,
  FormContainer,
  Title,
  ButtonContainer,
  CancelButton,
  SaveButton,
  ContainerLoading,
  Loader,
} from './styles';

const stringAtts = [
  'age',
  'gender',
  'ethnicity',
  'householdIncome',
  'workStatus',
  'industry',
  'seniority',
  'companySize',
  'locationState',
  'locationCountry',
];

const audienceAtts = {
  age,
  gender,
  ethnicity,
  householdIncome,
  workStatus,
  industry,
  seniority,
  companySize,
  locationCountry,
  locationState,
};

const TemplateAudience = (props) => {
  const {
    values,
    setValues,
    title,
    onCancel,
    setFieldValue,
    templateValues = {},
    loading,
    errors,
  } = props;

  useEffect(() => {
    const newForm = {};
    stringAtts.forEach((audienceAttribute) => {
      newForm[audienceAttribute] = {};
      newForm[audienceAttribute].Any = true;
    });

    const {
      audienceAttributes = [],
    } = templateValues;

    if (templateValues && !_.isEmpty(templateValues)) {
      newForm.templateName = templateValues.templateName;
      newForm.templateDescription = templateValues.templateDescription;
      newForm.device = templateValues.device;
      newForm.operatingSystem = templateValues.operatingSystem;
    }

    stringAtts.forEach((audienceAttribute) => {
      audienceAtts[audienceAttribute].forEach((item) => {
        let itemValue = item;
        if (audienceAttribute === 'locationCountry'
          || audienceAttribute === 'locationState') {
          itemValue = itemValue.value;
        }

        const field = _.find(audienceAttributes, (attribute) => (
          attribute.audienceAttributeType === _.snakeCase(audienceAttribute)
        ));

        if (field) {
          if (field.audienceAttributeType === 'age') {
            newForm[audienceAttribute].Any = false;
            field.audienceAttributeOptions.forEach((value) => {
              newForm[audienceAttribute][value.value] = true;
            });
          } else if (
            _.includes(
              _.map(field.audienceAttributeOptions, (value) => value.value),
              itemValue,
            )
            && itemValue !== 'Any'
          ) {
            newForm[audienceAttribute][itemValue] = true;
          } else if (itemValue !== 'Any') {
            newForm[audienceAttribute][itemValue] = false;
            newForm[audienceAttribute].Any = false;
          }
        } else {
          newForm[audienceAttribute][itemValue] = true;
        }
      });
    });

    const jobsSelected = _.filter(audienceAttributes, (attribute) => attribute.audienceAttributeType === 'job_role');
    newForm.jobsSelected = [];
    if (!_.isEmpty(jobsSelected)) {
      jobsSelected[0].audienceAttributeOptions.forEach((item) => {
        newForm.jobsSelected.push({ id: item.value });
      });
    }

    let questions = [];

    if (!_.isEmpty(templateValues) && !_.isEmpty(templateValues.questions)) {
      questions = templateValues.questions.map(({
        questionOptions, label: question, optionType, ...questionItem
      }) => ({
        ...questionItem,
        type: questionItem.questionType,
        question,
        answers: questionOptions.map(({ label: answer, ...option }) => {
          let type = 'May Select';
          if (option.disqualify) type = 'Disqualify';
          if (option.qualify) type = 'Qualify';
          return ({
            ...option,
            answer,
            type,
          });
        }),
      }));
    }

    setValues({
      ...values,
      ...newForm,
      questions,
      testerRequirements: templateValues.testerRequirements || '',
      language: templateValues.language ? { [templateValues.language]: true } : { 'en-US': true },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateValues]);

  return (
    <Container>
      <FormContainer>
        <FormikForm>
          <Form as="div">
            {title && (
              <Title>
                {title}
              </Title>
            )}

            {loading && (
              <ContainerLoading>
                <Loader data-loader="template-audience-loader" />
              </ContainerLoading>
            )}
            {!loading && (
              <>
                <ContainerDefault>
                  <Field
                    labelFontSize="14px"
                    labelColor={globalTheme.color.textLabel}
                    labelText="Template Name"
                    name="templateName"
                    placeholder="Enter name"
                    component={InputFormField}
                    width="50px"
                  />

                  <Field
                    labelFontSize="14px"
                    labelColor={globalTheme.color.textLabel}
                    labelText="Template Description"
                    name="templateDescription"
                    placeholder="Enter description"
                    component={InputFormField}
                  />
                </ContainerDefault>

                <Audience
                  values={values}
                  setValues={setValues}
                  setFieldValue={setFieldValue}
                  participantsSource={values.participantsSource}
                  errors={errors}
                />

                <ButtonContainer>
                  <CancelButton
                    onClick={onCancel}
                    loading={false}
                    type="button"
                  >
                    Cancel
                  </CancelButton>

                  <SaveButton
                    type="submit"
                    loading={false}
                  >
                    Save
                  </SaveButton>
                </ButtonContainer>
              </>
            )}

          </Form>
        </FormikForm>
      </FormContainer>
    </Container>
  );
};

TemplateAudience.propTypes = {
  values: PropTypes.shape({
    templateName: PropTypes.string,
    templateDescription: PropTypes.string,
    participants: PropTypes.string,
    participantsSource: PropTypes.string,
    projectType: PropTypes.string,
    testerRequirements: PropTypes.string,
    age: PropTypes.objectOf(PropTypes.bool),
    companySize: PropTypes.objectOf(PropTypes.bool),
    ethnicity: PropTypes.objectOf(PropTypes.bool),
    gender: PropTypes.objectOf(PropTypes.bool),
    householdIncome: PropTypes.objectOf(PropTypes.bool),
    industry: PropTypes.objectOf(PropTypes.bool),
    seniority: PropTypes.objectOf(PropTypes.bool),
    jobsSelected: PropTypes.objectOf(PropTypes.bool),
    locationState: PropTypes.objectOf(PropTypes.bool),
    locationCountry: PropTypes.objectOf(PropTypes.bool),
    workStatus: PropTypes.objectOf(PropTypes.bool),
    language: PropTypes.objectOf(PropTypes.bool),
    device: PropTypes.string,
    operatingSystem: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  setValues: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  title: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  templateValues: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool,
  errors: PropTypes.shape({
    device: PropTypes.string,
    operatingSystem: PropTypes.string,
  }).isRequired,
};

TemplateAudience.defaultProps = {
  title: undefined,
  templateValues: {},
  loading: false,
};

export default compose(
  withFormik({
    enableReinitialize: false,
    validationSchema: schema,
    mapPropsToValues: ({ formValues = {}, templateName, errors }) => ({
      templateName,
      participantsSource: 'playbook',
      testerRequirements: formValues.testerRequirements || '',
      age: formValues.age || {},
      companySize: formValues.companySize || {},
      ethnicity: formValues.ethnicity || {},
      gender: formValues.gender || {},
      householdIncome: formValues.householdIncome || {},
      industry: formValues.industry || {},
      seniority: formValues.seniority || {},
      jobsSelected: formValues.jobsSelected || {},
      questions: formValues.questions || [],
      locationState: formValues.locationState || {},
      locationCountry: formValues.locationCountry || {},
      workStatus: formValues.workStatus || {},
      language: formValues.language || { 'en-US': true },
      device: formValues.device || 'computer',
      operatingSystem: formValues.operatingSystem || ['windows', 'macos'],
      errors,
    }),
    handleSubmit: (values, { props }) => {
      const {
        onSave,
        templateValues = {},
      } = props;

      const {
        templateName,
        templateDescription,
        device,
        operatingSystem,
        context,
        questions,
        testerRequirements,
        language,
      } = values;

      const audienceFields = [
        'age',
        'gender',
        'companySize',
        'ethnicity',
        'householdIncome',
        'industry',
        'seniority',
        'workStatus',
        'locationState',
        'locationCountry',
        'jobsSelected',
      ];

      let audienceAttributesAttributes = [];

      if (!_.isEmpty(values.age)) {
        audienceAttributesAttributes = audienceFields.map((field) => {
          const audienceAttributeOptionsAttributes = [];
          let audienceAttributeType = _.snakeCase(field);

          const {
            audienceAttributes,
          } = templateValues;

          let previousValues = [];

          if (field === 'jobsSelected') {
            audienceAttributeType = 'job_role';
            let prevValues = [];

            // previous values
            if (templateValues) {
              prevValues = _.filter(
                templateValues.audienceAttributes, (att) => att.audienceAttributeType === audienceAttributeType,
              );
            }

            if (_.isEmpty(prevValues)) {
              _.map(values[field], (value) => {
                audienceAttributeOptionsAttributes.push({ value: value.id });
              });
            }
            if (!_.isEmpty(prevValues)) {
              _.map(values[field], (value) => {
                if (_.isEmpty(_.filter(prevValues[0].audienceAttributeOptions, (opt) => (
                  opt.value === value.id
                )))) {
                  audienceAttributeOptionsAttributes.push({ value: value.id });
                }
              });
              _.map(prevValues[0].audienceAttributeOptions, (value) => {
                if (_.isEmpty(_.filter(values[field], (opt) => opt.id === value.value))) {
                  audienceAttributeOptionsAttributes.push({ id: value.id, _destroy: true });
                }
              });
            }
          }

          // previous values
          previousValues = _.filter(
            audienceAttributes, (att) => att.audienceAttributeType === audienceAttributeType,
          );

          if (values[field].Any && !_.isEmpty(previousValues)) {
            // destroy if new values is any

            return {
              id: previousValues[0].id,
              audienceAttributeType,
              _destroy: true,
            };
          }

          if (!values[field].Any) {
            _.map(values[field], (value, key) => {
              if (value && key !== 'Any'
                && field !== 'jobsSelected') {
                if (_.isEmpty(previousValues) || (
                  !_.isEmpty(previousValues)
                  && _.isEmpty(
                    _.filter(previousValues[0].audienceAttributeOptions, (opt) => opt.value === key),
                  )
                )) {
                  audienceAttributeOptionsAttributes.push({ value: key });
                }
              }
            });
          }

          // destroy unchecked values
          if (!_.isEmpty(previousValues)) {
            previousValues[0].audienceAttributeOptions.forEach((item) => {
              const previousOptions = _.map(values[field], (option, key) => {
                if (option) {
                  return key;
                }
                return null;
              });
              if (!_.includes(previousOptions, item.value) && field !== 'jobsSelected') {
                audienceAttributeOptionsAttributes.push({ id: item.id, _destroy: true });
              }
            });
          }

          if (_.isEmpty(audienceAttributeOptionsAttributes)) {
            return null;
          }

          return {
            ...(!_.isEmpty(previousValues) && { id: previousValues[0].id }),
            audienceAttributeType,
            audienceAttributeOptionsAttributes,
          };
        });
      }

      let questionsAttributes = [];

      questionsAttributes = _.chain(questions).map((question, index) => {
        const questionOptionsAttributes = question.answers.map((answer, optionIndex) => ({
          ...(answer.id && { id: answer.id }),
          label: answer.answer,
          disqualify: answer.type === 'Disqualify',
          qualify: answer.type === 'Qualify',
          optionType: answer.optionType,
          order: optionIndex,
        }));

        const previousQuestion = _.find(templateValues.questions, ({ id }) => id === question.id);

        let deletedAnswers = [];

        if (previousQuestion && !_.isEmpty(previousQuestion)) {
          deletedAnswers = _.difference(
            _.map(previousQuestion.questionOptions, (answer) => answer.id),
            _.map(question.answers, (answer) => answer.id),
          );
        }

        if (!_.isEmpty(deletedAnswers)) {
          deletedAnswers.forEach((questionId) => {
            questionOptionsAttributes.push({
              id: questionId,
              _destroy: true,
            });
          });
        }

        return {
          ...(question.id && { id: question.id }),
          questionOptionsAttributes,
          label: question.question,
          questionType: question.type,
          questionOrder: index,
          randomize: question.randomize,
        };
      })
        .sortBy('questionOrder')
        .value();

      const deletedQuestions = _.difference(
        _.map(templateValues.questions, (question) => question.id),
        _.map(values.questions, (question) => question.id),
      );

      if (!_.isEmpty(deletedQuestions)) {
        deletedQuestions.forEach((questionId) => {
          questionsAttributes.push({
            id: questionId,
            _destroy: true,
          });
        });
      }

      const surveyAttributes = {
        surveyType: 'screener',
        questionsAttributes,
      };

      audienceAttributesAttributes = _.filter(audienceAttributesAttributes, (att) => att !== null);

      if (templateValues.surveyId) {
        surveyAttributes.id = templateValues.surveyId;
      }
      const newValues = {
        name: templateName,
        description: templateDescription,
        device,
        operatingSystem,
        templateContext: context,
        surveyAttributes,
        audienceAttributesAttributes,
        templateType: 'audience',
        testerRequirements,
        language: _.findKey(language, _.identity),
      };
      onSave(newValues);
    },
  }),
)(TemplateAudience);
